import type { CountryCode, Occupation } from "./types";

export interface CountryPack {
  name: string; cur: string; verified: boolean; source: string;
  minRate: number | null;
  weekly: number; daily: number; dailySixDay: number;
  breakMins: number; breakAfter: number;
  ot: number; sunday: number;
  leave: {
    /** Working days per annual leave cycle. */
    annual: number;
    /** Paid sick days per SICK cycle, which is NOT a year in every country.
     *  South Africa's is 36 months, so labelling this "per year" overstates a
     *  worker's entitlement threefold — see sickCycleMonths. */
    sick: number;
    /** Days per annual leave cycle. */
    family: number;
  };
  /** Length of one sick leave cycle in months. */
  sickCycleMonths: number;
  /** What a bank account looks like here. A six-digit branch code is a South
   *  African fact, not a universal one, so it lives with the country rather
   *  than in a form. */
  banking: {
    /** What the routing number is called to the person typing it. */
    branchLabel: string;
    branchDigits: number | null;
    accountDigits: [min: number, max: number];
  };
  note: string;
}

/** Starting defaults only — every value is overridable per employee.
 *
 *  A pack existing here does NOT mean the app can sign anyone up in that
 *  country. Sign-in is phone OTP, and Twilio Geo Permissions currently allow
 *  South Africa only — everywhere else is rejected before the message leaves
 *  Twilio. Sender ID rules also differ per country. See the Twilio geo
 *  permissions section in the README before enabling a new market.
 *  `verified: false` means nobody has checked it against the current wage
 *  order, and the UI must say so rather than imply authority it lacks. */
export const COUNTRIES: Record<CountryCode, CountryPack> = {
  ZA: {
    name: "South Africa", cur: "R", verified: true,
    source: "National Minimum Wage Act · Gazette 54075 · from 1 Mar 2026; leave per BCEA",
    minRate: 30.23, weekly: 45, daily: 9, dailySixDay: 8,
    breakMins: 60, breakAfter: 5, ot: 1.5, sunday: 2,
    leave: { annual: 15, sick: 30, family: 3 },
    sickCycleMonths: 36,
    banking: { branchLabel: "Branch code", branchDigits: 6, accountDigits: [6, 13] },
    note: "Domestic and garden workers in a private household are on full parity with the national minimum wage. Sick leave is 30 days per 36-month cycle, not per year. Family responsibility leave needs 4 months' service at 4+ days a week.",
  },
  NA: {
    name: "Namibia", cur: "N$", verified: false, source: "Labour Act 11 of 2007",
    minRate: null, weekly: 45, daily: 9, dailySixDay: 8,
    breakMins: 60, breakAfter: 5, ot: 1.5, sunday: 2,
    leave: { annual: 20, sick: 30, family: 5 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Branch code", branchDigits: 6, accountDigits: [6, 13] },
    note: "Not checked against the current wage order. Confirm before you pay on these numbers.",
  },
  BW: {
    name: "Botswana", cur: "P", verified: false, source: "Employment Act",
    minRate: null, weekly: 48, daily: 8, dailySixDay: 8,
    breakMins: 60, breakAfter: 5, ot: 1.5, sunday: 2,
    leave: { annual: 15, sick: 20, family: 3 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Branch code", branchDigits: 6, accountDigits: [6, 13] },
    note: "Not checked against the current wage order. Confirm before you pay on these numbers.",
  },
  ZW: {
    name: "Zimbabwe", cur: "US$", verified: false, source: "Labour Act · domestic workers SI",
    minRate: null, weekly: 45, daily: 9, dailySixDay: 8,
    breakMins: 60, breakAfter: 5, ot: 1.5, sunday: 2,
    leave: { annual: 22, sick: 90, family: 3 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Branch code", branchDigits: null, accountDigits: [6, 16] },
    note: "Not checked against the current statutory instrument. Confirm before you pay on these numbers.",
  },
  KE: {
    name: "Kenya", cur: "KSh", verified: false, source: "Employment Act · Regulation of Wages Order",
    minRate: null, weekly: 52, daily: 9, dailySixDay: 8,
    breakMins: 60, breakAfter: 5, ot: 1.5, sunday: 2,
    leave: { annual: 21, sick: 14, family: 3 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Branch code", branchDigits: null, accountDigits: [6, 16] },
    note: "Not checked against the current wages order. Confirm before you pay on these numbers.",
  },
  GB: {
    name: "United Kingdom", cur: "£", verified: false, source: "Working Time Regulations · NMW",
    minRate: null, weekly: 48, daily: 8, dailySixDay: 8,
    breakMins: 20, breakAfter: 6, ot: 1, sunday: 1,
    leave: { annual: 28, sick: 0, family: 0 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Sort code", branchDigits: 6, accountDigits: [8, 8] },
    note: "There is no statutory overtime premium — whatever you agree is what applies.",
  },
  OTHER: {
    name: "Somewhere else", cur: "", verified: false, source: "No rule pack",
    minRate: null, weekly: 45, daily: 8, dailySixDay: 8,
    breakMins: 30, breakAfter: 5, ot: 1.5, sunday: 1.5,
    leave: { annual: 15, sick: 10, family: 0 },
    sickCycleMonths: 12,
    banking: { branchLabel: "Branch or routing code", branchDigits: null, accountDigits: [4, 20] },
    note: "No rules on file for this country. Set every value by hand.",
  },
};

export const OCCUPATIONS: Record<Occupation, { label: string; days: number; blurb: string }> = {
  domestic: { label: "Domestic", days: 5, blurb: "Cleaning, laundry, cooking" },
  childcare: { label: "Childcare", days: 5, blurb: "Nanny, au pair, after-care" },
  garden: { label: "Garden", days: 2, blurb: "Grounds, pool, maintenance" },
  caregiver: { label: "Care", days: 5, blurb: "Elderly or frail care" },

  driver: { label: "Driver", days: 5, blurb: "School runs, errands" },
  cook: { label: "Cook", days: 5, blurb: "Kitchen and meals" },
  housekeeper: { label: "Housekeeper", days: 5, blurb: "Running the household" },
  au_pair: { label: "Au pair", days: 5, blurb: "Live-in childcare" },
  security: { label: "Security", days: 5, blurb: "Gate and grounds" },
  handyman: { label: "Handyman", days: 2, blurb: "Repairs and maintenance" },
  farm: { label: "Farm worker", days: 5, blurb: "Smallholding or plot" },
  laundry: { label: "Laundry", days: 2, blurb: "Washing and ironing" },

  other: { label: "Other", days: 5, blurb: "Something else" },
};

/**
 * The four on the front of the screen. These are the work this app is for, and
 * putting thirteen tiles up would bury them in a list nobody reads.
 */
export const MAIN_OCCUPATIONS: Occupation[] = ["domestic", "childcare", "garden", "driver"];

/** Everything else the list knows, for the picker behind "Something else". */
export const MORE_OCCUPATIONS: Occupation[] = [
  "caregiver", "cook", "housekeeper", "au_pair", "security", "handyman", "farm", "laundry",
];

/**
 * What to print for somebody's job.
 *
 * Their own words win when the job is "other", because that is the whole point
 * of having asked. Everything else prints the label rather than the enum, so a
 * register says "Au pair" and not "au_pair".
 */
export function occupationLabel(occupation: Occupation, other?: string | null): string {
  if (occupation === "other") {
    const words = (other ?? "").trim();
    return words || "Other";
  }
  return OCCUPATIONS[occupation]?.label ?? occupation;
}

export function defaultsFor(code: CountryCode, occupation: Occupation) {
  const c = COUNTRIES[code] ?? COUNTRIES.OTHER;
  const o = OCCUPATIONS[occupation] ?? OCCUPATIONS.domestic;
  return {
    rate: c.minRate ?? 0,
    currency: c.cur,
    ordinaryDaily: o.days > 5 ? c.dailySixDay : c.daily,
    ordinaryWeekly: c.weekly,
    breakMins: c.breakMins,
    breakAfter: c.breakAfter,
    ot: c.ot,
    sunday: c.sunday,
    daysPerWeek: o.days,
    // Shaped for an employment_terms row rather than for display: this is what
    // an employer's "add employee" flow should prefill, and every value stays
    // editable from there.
    annualLeaveDays: c.leave.annual,
    sickLeaveDays: c.leave.sick,
    sickCycleMonths: c.sickCycleMonths,
    familyLeaveDays: c.leave.family,
  };
}
